export type SubgroupOrderEntry = {
  id: string;
  subgroupDisplayOrder: number | null;
};

export type SubgroupDraftEntry = {
  groupId: string;
  subgroupId: string;
  subgroupDisplayOrder: number | null;
};

export type SubgroupCardEntry = {
  groupId?: string | null;
  subgroupId?: string | null;
  subgroupDisplayOrder?: number | null;
};

const compareSubgroupOrder = (left: SubgroupOrderEntry, right: SubgroupOrderEntry): number => {
  const leftOrder = left.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
  const rightOrder = right.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }
  return left.id.localeCompare(right.id);
};

export const buildSubgroupOrderUpdates = (
  subgroups: SubgroupOrderEntry[],
  sourceId: string,
  targetId: string
): Record<string, number> => {
  const source = subgroups.find((subgroup) => subgroup.id === sourceId);
  const target = subgroups.find((subgroup) => subgroup.id === targetId);
  if (!source || !target || sourceId === targetId) {
    return {};
  }

  if (
    source.subgroupDisplayOrder !== null &&
    target.subgroupDisplayOrder !== null &&
    source.subgroupDisplayOrder !== target.subgroupDisplayOrder
  ) {
    return {
      [sourceId]: target.subgroupDisplayOrder,
      [targetId]: source.subgroupDisplayOrder,
    };
  }

  const normalizedIds = [...subgroups].sort(compareSubgroupOrder).map((subgroup) => subgroup.id);
  const sourceIndex = normalizedIds.indexOf(sourceId);
  const targetIndex = normalizedIds.indexOf(targetId);
  if (sourceIndex === -1 || targetIndex === -1) {
    return {};
  }

  [normalizedIds[sourceIndex], normalizedIds[targetIndex]] = [
    normalizedIds[targetIndex],
    normalizedIds[sourceIndex],
  ];
  return normalizedIds.reduce<Record<string, number>>((updates, subgroupId, index) => {
    updates[subgroupId] = index;
    return updates;
  }, {});
};

export const buildSubgroupDragOrderUpdates = (
  subgroups: SubgroupOrderEntry[],
  sourceId: string,
  targetId: string
): Record<string, number> => {
  if (sourceId === targetId) {
    return {};
  }

  const orderedIds = [...subgroups].sort(compareSubgroupOrder).map((subgroup) => subgroup.id);
  const sourceIndex = orderedIds.indexOf(sourceId);
  const targetIndex = orderedIds.indexOf(targetId);
  if (sourceIndex === -1 || targetIndex === -1) {
    return {};
  }

  const [movedId] = orderedIds.splice(sourceIndex, 1);
  orderedIds.splice(targetIndex, 0, movedId);
  return orderedIds.reduce<Record<string, number>>((updates, subgroupId, index) => {
    updates[subgroupId] = index;
    return updates;
  }, {});
};

export const applySubgroupOrderToCards = <T extends SubgroupCardEntry>(
  cards: T[],
  groupId: string,
  orderUpdates: Record<string, number>
): T[] =>
  cards.map((card) => {
    const subgroupId = (card.subgroupId ?? '').trim();
    if (
      (card.groupId ?? '').trim() !== groupId ||
      !Object.prototype.hasOwnProperty.call(orderUpdates, subgroupId)
    ) {
      return card;
    }
    return {
      ...card,
      subgroupDisplayOrder: orderUpdates[subgroupId],
    };
  });

export const mergeSavedSubgroupDrafts = <T extends SubgroupDraftEntry>(
  currentDrafts: T[],
  savedDrafts: T[],
  groupId: string,
  savedSubgroupIds: Set<string>,
  sortDrafts: (drafts: T[]) => T[]
): T[] =>
  sortDrafts([
    ...currentDrafts.filter(
      (subgroup) => subgroup.groupId !== groupId || !savedSubgroupIds.has(subgroup.subgroupId)
    ),
    ...savedDrafts.filter(
      (subgroup) => subgroup.groupId === groupId && savedSubgroupIds.has(subgroup.subgroupId)
    ),
  ]);

export const findEmptyDraftSubgroup = <T extends SubgroupDraftEntry, C extends SubgroupCardEntry>(
  subgroups: T[],
  cards: C[]
): T | undefined =>
  subgroups.find(
    (subgroup) =>
      !cards.some(
        (card) =>
          (card.groupId ?? '').trim() === subgroup.groupId &&
          (card.subgroupId ?? '').trim() === subgroup.subgroupId
      )
  );

export const resolveSubgroupName = (metadataName: string | null | undefined, formName: string): string =>
  metadataName?.trim() || formName.trim();

export const resolveSubgroupSchedule = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): { startDate: string | null; endDate: string | null; hasOverride: boolean } => ({
  startDate: startDate ?? null,
  endDate: endDate ?? null,
  hasOverride: Boolean(startDate || endDate),
});
